// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://bit.ly/CRA-PWA

interface ServiceWorkerConfig {
    onUpdate?: (registration: ServiceWorkerRegistration) => void;
    onSuccess?: (registration: ServiceWorkerRegistration) => void;
}

const LOCALHOST_IPV4_REGEX = /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/;

const isLocalhost = Boolean(
    globalThis.location.hostname === 'localhost' ||
        // [::1] is the IPv6 localhost address.
        globalThis.location.hostname === '[::1]' ||
        // 127.0.0.0/8 are considered localhost for IPv4.
        LOCALHOST_IPV4_REGEX.exec(globalThis.location.hostname),
);

export function register(config?: ServiceWorkerConfig) {
    if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) return;

    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL ?? '', globalThis.location.href);
    if (publicUrl.origin !== globalThis.location.origin) {
        // Our service worker won't work if PUBLIC_URL is on a different origin
        // from what our page is served on. This might happen if a CDN is used to
        // serve assets; see https://github.com/facebook/create-react-app/issues/2374
        return;
    }

    globalThis.addEventListener('load', () => {
        const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

        if (isLocalhost) {
            // This is running on localhost. Let's check if a service worker still exists or not.
            checkValidServiceWorker(swUrl, config);

            // Add some additional logging to localhost, pointing developers to the
            // service worker/PWA documentation.
            navigator.serviceWorker.ready.then(() => {
                console.log(
                    'This web app is being served cache-first by a service ' +
                        'worker. To learn more, visit https://bit.ly/CRA-PWA',
                );
            });
        } else {
            // Is not localhost. Just register service worker
            registerValidSW(swUrl, config);
        }
    });
}

function registerValidSW(swUrl: string, config?: ServiceWorkerConfig) {
    navigator.serviceWorker
        .register(swUrl)
        .then(registration => {
            registration.onupdatefound = () => {
                const installingWorker = registration.installing;
                if (installingWorker == null) return;

                installingWorker.onstatechange = () => {
                    if (installingWorker.state !== 'installed') return;

                    if (navigator.serviceWorker.controller) {
                        console.log(
                            'New content is available and will be used when all ' +
                                'tabs for this page are closed. See https://bit.ly/CRA-PWA.',
                        );
                        config?.onUpdate?.(registration);
                    } else {
                        console.log('Content is cached for offline use.');
                        config?.onSuccess?.(registration);
                    }
                };
            };
        })
        .catch(error => {
            console.error('Error during service worker registration:', error);
        });
}

function checkValidServiceWorker(swUrl: string, config?: ServiceWorkerConfig) {
    fetch(swUrl, {
        headers: { 'Service-Worker': 'script' },
    })
        .then(response => {
            const contentType = response.headers.get('content-type');
            const isInvalidWorker =
                response.status === 404 ||
                (contentType != null && !contentType.includes('javascript'));

            if (isInvalidWorker) {
                navigator.serviceWorker.ready.then(registration => {
                    registration.unregister().then(() => {
                        globalThis.location.reload();
                    });
                });
            } else {
                registerValidSW(swUrl, config);
            }
        })
        .catch(() => {
            console.log(
                'No internet connection found. App is running in offline mode.',
            );
        });
}

export function unregister() {
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.ready
        .then(registration => {
            registration.unregister();
        })
        .catch(error => {
            console.error(error.message);
        });
}
